import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  getMarketplaceBookingLedgerTimeline,
  getMarketplaceBookingReceipts,
  listMyMarketplaceBookings,
} from "../../api/myboothshareBilling";
import { useAuth } from "../../context/AuthContext";
import MarketplaceShell from "./MarketplaceShell";
import {
  formatMarketplaceDateTime,
  formatMarketplaceMoneyFromCents,
  getMarketplaceReleaseLabel,
  getWorkspaceFinanceStateLabel,
} from "../../utils/myboothshareBillingPresentation";

function safeRows(rows) {
  return Array.isArray(rows) ? rows : [];
}

function cents(value) {
  const amount = Number(value || 0);
  return Number.isFinite(amount) ? amount : 0;
}

function PaymentTile({ label, value, detail }) {
  return (
    <article className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </div>
      <div className="mt-3 text-2xl font-semibold text-slate-950">{value}</div>
      {detail ? <div className="mt-2 text-sm leading-6 text-slate-600">{detail}</div> : null}
    </article>
  );
}

function getFinanceStatus(item = {}, timeline = null) {
  const value =
    item.finance_state_code ||
    timeline?.finance_state ||
    item.payment_status ||
    item.transfer_status ||
    "";
  if (item.dispute_open) return "Disputed";
  return getWorkspaceFinanceStateLabel(value);
}

export default function MarketplaceAccountPaymentsPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [financeByRental, setFinanceByRental] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError("");
    listMyMarketplaceBookings(token)
      .then((response) => {
        if (active) {
          setItems(Array.isArray(response?.items) ? response.items : []);
        }
      })
      .catch((err) => {
        if (active) {
          setError(
            err?.response?.data?.detail ||
              err.message ||
              "Unable to load your Ellyanna Boothshare payments."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (!token || !items.length) return undefined;
    let active = true;
    Promise.all(
      items.map(async (item) => {
        try {
          const [timeline, receipts] = await Promise.all([
            getMarketplaceBookingLedgerTimeline(token, item.booking_id),
            getMarketplaceBookingReceipts(token, item.booking_id),
          ]);
          return [item.booking_id, { timeline, receipts }];
        } catch (_) {
          return [item.booking_id, { timeline: null, receipts: null }];
        }
      })
    ).then((rows) => {
      if (!active) return;
      setFinanceByRental(
        rows.reduce((acc, [rentalId, value]) => {
          acc[rentalId] = value;
          return acc;
        }, {})
      );
    });
    return () => {
      active = false;
    };
  }, [items, token]);

  const metrics = useMemo(() => {
    const dueNow = items.reduce(
      (sum, item) => sum + cents(item.due_now_cents || item.finance_state?.amount_due_now_cents),
      0
    );
    const depositsHeld = items.reduce(
      (sum, item) => sum + cents(item.deposit_amount_cents || item.finance_state?.deposit_amount_cents),
      0
    );
    const paid = items.reduce((sum, item) => sum + cents(item.amount_cents), 0);
    const refunds = items.reduce((sum, item) => sum + cents(item.refund_total_cents), 0);
    const disputes = items.filter((item) => item.dispute_open).length;
    return { dueNow, depositsHeld, paid, refunds, disputes };
  }, [items]);

  return (
    <MarketplaceShell>
      <section className="space-y-6">
        <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="bg-[#32386f] px-7 py-7 text-white lg:px-9">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-rose-200">
              Ellyanna Boothshare Payments
            </p>
            <h1 className="mt-4 text-4xl font-semibold tracking-tight lg:text-[3rem]">
              Payments
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-8 text-slate-200">
              Review payment required states, deposit holds, receipts, refunds,
              payout release timing, and disputes for your rental agreements.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/account/rentals"
                className="rounded-full border border-white/15 bg-white/[0.06] px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/[0.12]"
              >
                My Rentals
              </Link>
              <Link
                to="/search"
                className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#2f3566] hover:bg-slate-100"
              >
                Search Workspaces
              </Link>
            </div>
          </div>

          <div className="space-y-6 px-7 py-7 lg:px-9">
            <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <PaymentTile
                label="Payment Required"
                value={formatMarketplaceMoneyFromCents(metrics.dueNow, "cad")}
                detail="Amounts due now across your rentals."
              />
              <PaymentTile
                label="Deposit Held"
                value={formatMarketplaceMoneyFromCents(metrics.depositsHeld, "cad")}
                detail="Deposits retained until release conditions are met."
              />
              <PaymentTile
                label="Payment Captured"
                value={formatMarketplaceMoneyFromCents(metrics.paid, "cad")}
                detail="Total paid through Ellyanna Boothshare."
              />
              <PaymentTile
                label="Refunded"
                value={formatMarketplaceMoneyFromCents(metrics.refunds, "cad")}
                detail="Refunds recorded on your rental agreements."
              />
              <PaymentTile
                label="Disputed"
                value={metrics.disputes}
                detail="Open disputes pause payout release."
              />
            </section>

            {!token ? (
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-600">
                Sign in to see your Ellyanna Boothshare payment history.
              </div>
            ) : null}
            {loading ? <p className="text-sm text-slate-600">Loading payments...</p> : null}
            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
            {!loading && !error && token && !items.length ? (
              <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-sm text-slate-600">
                Payments will appear after you request or pay for a workspace rental.
              </div>
            ) : null}

            <section className="grid gap-5">
              {items.map((item) => {
                const financeRecord = financeByRental[item.booking_id] || {};
                const timeline = financeRecord.timeline || null;
                const receipts = financeRecord.receipts || { receipts: [], installments: [] };
                const recentEntries = safeRows(timeline?.entries).slice(-4).reverse();
                return (
                  <article
                    key={item.booking_id}
                    className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-500">
                          Rental Agreement #{item.booking_id}
                        </p>
                        <h2 className="mt-2 text-2xl font-semibold text-slate-950">
                          {item.workspace_name || `Workspace #${item.workspace_id || "n/a"}`}
                        </h2>
                        <p className="mt-2 text-sm text-slate-600">
                          {formatMarketplaceDateTime(item.start_at_utc)} to{" "}
                          {formatMarketplaceDateTime(item.end_at_utc)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
                          {getFinanceStatus(item, timeline)}
                        </span>
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                          {getMarketplaceReleaseLabel(item)}
                        </span>
                      </div>
                    </div>

                    <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <PaymentTile
                        label="Payment Captured"
                        value={formatMarketplaceMoneyFromCents(item.amount_cents, item.currency)}
                        detail="Collected through checkout."
                      />
                      <PaymentTile
                        label="Payout Pending"
                        value={formatMarketplaceMoneyFromCents(item.net_payout_cents, item.currency)}
                        detail="Release waits for conditions and review windows."
                      />
                      <PaymentTile
                        label="Refunded"
                        value={formatMarketplaceMoneyFromCents(item.refund_total_cents, item.currency)}
                        detail="Refund activity attached to this rental."
                      />
                      <PaymentTile
                        label="Estimated Release"
                        value={
                          item.released_at_utc
                            ? formatMarketplaceDateTime(item.released_at_utc)
                            : item.scheduled_release_at_utc
                            ? formatMarketplaceDateTime(item.scheduled_release_at_utc)
                            : "Pending"
                        }
                        detail="May pause if a dispute is opened."
                      />
                    </div>

                    <div className="mt-5 grid gap-4 xl:grid-cols-2">
                      <section className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Receipts
                        </p>
                        <div className="mt-4 space-y-3">
                          {safeRows(receipts.receipts).slice(0, 4).map((receipt) => (
                            <div
                              key={receipt.id}
                              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
                            >
                              <div className="font-semibold text-slate-950">
                                {receipt.title || "Receipt"}
                              </div>
                              <div className="mt-1">
                                {formatMarketplaceMoneyFromCents(receipt.amount_cents, receipt.currency)} -{" "}
                                {formatMarketplaceDateTime(receipt.issued_at)}
                              </div>
                            </div>
                          ))}
                          {!safeRows(receipts.receipts).length ? (
                            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-6 text-sm text-slate-500">
                              Receipts will appear here as payments, refunds, and statements are issued.
                            </div>
                          ) : null}
                        </div>
                      </section>

                      <section className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Ledger
                        </p>
                        <div className="mt-4 space-y-3">
                          {recentEntries.map((entry) => (
                            <div
                              key={entry.id}
                              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
                            >
                              <div className="font-semibold text-slate-950">
                                {entry.description || entry.entry_type}
                              </div>
                              <div className="mt-1">{formatMarketplaceDateTime(entry.occurred_at)}</div>
                            </div>
                          ))}
                          {!recentEntries.length ? (
                            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-6 text-sm text-slate-500">
                              Ledger activity will appear after payment events are recorded.
                            </div>
                          ) : null}
                        </div>
                      </section>
                    </div>

                    {item.dispute_open ? (
                      <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        This rental is disputed, so payout release is paused while the case is reviewed.
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </section>
          </div>
        </article>
      </section>
    </MarketplaceShell>
  );
}
